<template>
  <span
    class="tag"
    :data-hj-allow="hjAllowed"
    :class="{
      'tag--alert': alert,
      'tag--image': image,
      'tag--excl': exclusive,
      'tag--excl-large': exclusiveLarge,
      'tag--alert-detail': alertDetail,
      'tag--countdown': countdown,
      'tag--custom-color': Boolean(customColorCode) || Boolean(customColorCodeText),
      'tag--hint': hint,
      'tag--payback-dark': paybackDark,
      'tag--payback-light': paybackLight,
      'tag--payback-light-disabled': paybackLightDisabled,
      'tag--large': large,
      'tag--removable': removable,
      'tag--orange': orange,
      'tag--sand': sand,
      'tag--new-item': newItem,
      'tag--new-product': newProduct,
      'tag--small': small,
      'tag--pb-offer': paybackOffer,
      'tag--price-tip': priceTip,
      'tag--offer': specialOffer,
      'tag--info': info,
      'tag--bold': fontBold,
      'tag--advice': advice,
      'tag--new-info': newInfo,
      'tag--promotion': promotion,
      'tag--discount': discount,
      'tag--deal': deal,
      'tag--friends': friends,
      'tag--delivery-type': deliveryType,
    }"
    :style="colorStyles"
  >
    <slot> </slot>
    <fn-anchor
      v-if="removable"
      @click="$emit('remove')"
    >
      <fn-icon icon="trash" />
    </fn-anchor>
  </span>
</template>

<script>
export default defineComponent({
  props: {
    alert: {
      type: Boolean,
    },
    alertDetail: {
      type: Boolean,
    },
    customColorCode: {
      type: String,
      default: '',
    },
    customColorCodeText: {
      type: String,
      default: '',
    },
    image: {
      type: Boolean,
    },
    exclusive: {
      type: Boolean,
    },
    exclusiveLarge: {
      type: Boolean,
    },
    countdown: {
      type: Boolean,
    },
    hint: {
      type: Boolean,
    },
    paybackDark: {
      type: Boolean,
    },
    paybackLight: {
      type: Boolean,
    },
    paybackLightDisabled: {
      type: Boolean,
    },
    removable: {
      type: Boolean,
    },
    large: {
      type: Boolean,
    },
    orange: {
      type: Boolean,
    },
    sand: {
      type: Boolean,
    },
    newItem: {
      type: Boolean,
    },
    newProduct: {
      type: Boolean,
    },
    payback: {
      type: Boolean,
    },
    small: {
      type: Boolean,
    },
    paybackOffer: {
      type: Boolean,
    },
    priceTip: {
      type: Boolean,
    },
    specialOffer: {
      type: Boolean,
    },
    info: {
      type: Boolean,
    },
    fontBold: {
      type: Boolean,
    },
    advice: {
      type: Boolean,
    },
    newInfo: {
      type: Boolean,
    },
    hjAllowed: {
      type: Boolean,
    },
    promotion: {
      type: Boolean,
    },
    discount: {
      type: Boolean,
    },
    deal: {
      type: Boolean,
    },
    friends: {
      type: Boolean,
    },
    deliveryType: {
      type: Boolean,
    },
  },
  emits: ['remove'],
  computed: {
    ...mapState(useMainStore, {
      features: 'getFeature',
    }),

    colorStyles() {
      const styling = {};

      // Only apply for discount tags, values are hardcoded until
      // we have campaigns with custom colors defined in BE
      if (this.alertDetail && this.features('blackWeekEnabled')) {
        styling.color = '#fff';
        styling.backgroundColor = '#000';
        styling.borderColor = '#000';
      }

      if (this.customColorCodeText?.length) {
        styling.color = this.customColorCodeText;
      }

      if (this.customColorCode?.length) {
        styling.backgroundColor = this.customColorCode;
        styling.borderColor = 'transparent';
      }

      return styling;
    },

    specialOfferSrc() {
      if (this.$i18n.locale === 'de' || this.$i18n.locale === 'at' || this.$i18n.locale === 'lu') {
        return useImageAssets('special-offer.svg').src || '';
      }

      return useImageAssets(`special-offer-${this.$i18n.locale}.svg`).src || '';
    },
  },
});
</script>

<style lang="scss" scoped>
.tag {
  position: relative;
  display: inline;
  font-size: map-get($font-sizes, 's');
  padding: 0.56em 0.83em;
  border: 1px solid palette(grey, base);
  border-radius: math.div($pad, 4);
  background-color: $color-general-background-neutral-default;
  -webkit-font-smoothing: antialiased;

  .tag-subline {
    text-transform: uppercase;
    font-weight: 900;
  }

  .anchor {
    position: absolute;
    right: $pad;
    top: 0;
    bottom: 0;
    height: 24px;
    margin: auto 0;
  }

  &.tag--small {
    padding: 0.3em 0.83em;
    line-height: 1;
  }

  &.tag--removable {
    padding-right: $pad * 3;
  }

  &.tag--alert {
    color: palette(red);
    border: 1px solid palette(red);
  }

  &.tag--alert-detail {
    padding: 2px 6px;
    border: none;
    background-color: $color-general-background-loyalty-friends-secondary-default;
    color: $color-general-text-inverse-default;
    font-size: map-get($font-sizes, 's');
    font-weight: map-get($font-weights, 'bold');
    text-transform: uppercase;
  }

  &.tag--image {
    display: inline-flex;
    padding: 0;
  }

  &.tag--excl,
  &.tag--excl-large {
    border: none;
    background-color: $color-brand-fnmz-green;
    color: $color-general-text-inverse-default;
    font-size: map-get($font-sizes, 's');
  }

  &.tag--excl {
    display: block;
    padding: math.div($pad, 4) !important;
    line-height: 1;
  }

  &.tag--excl-large {
    padding: math.div($pad, 4);
    font-weight: map-get($font-weights, 'bold');

    :deep(br) {
      display: none;
    }
  }

  &.tag--pb-offer {
    display: inline-flex;
    height: 100%;
    padding: 0 !important;
    border: none;
    line-height: 1em;
    color: $color-general-text-inverse-default;
    background-color: transparent;

    img {
      height: 100%;
    }
  }

  &.tag--price-tip {
    padding: 2px 6px;
    border: none;
    font-size: map-get($font-sizes, 's');
    font-weight: map-get($font-weights, 'bold');
    text-transform: uppercase;
    background-color: $color-orange-1800;
    color: $color-general-text-inverse-default;

    img {
      height: 100%;
    }
  }

  &.tag--offer {
    display: inline-flex;
    padding: 0 !important;
    border: none;
    line-height: 1em;
    color: $color-general-text-inverse-default;
    background-color: transparent;

    img {
      height: 100%;
    }
  }

  &.tag--countdown {
    color: palette(red);
    font-size: map-get($font-sizes, 'm');
    padding: 0.36em 0.71em;
  }

  &.tag--custom-color {
    border-radius: map-get($border-radiuses, 'l');
    font-size: map-get($font-sizes, 'm');
    padding: math.div($pad, 4) math.div($pad, 2);
  }

  &.tag--hint {
    display: inline-block;
    padding: 7px;
    line-height: 1.1em;
    border: none;
    border-bottom-right-radius: 0;
    border-top-right-radius: 0;
    background-color: palette(orange);
    color: $color-general-text-inverse-default;
    text-transform: uppercase;
    font-weight: 900;
  }

  &.tag--orange {
    padding: 2px 6px;
    background-color: palette(orange);
    color: $color-general-text-inverse-default;
    text-transform: uppercase;
    border: 0;
    border-radius: 2px;
    font-size: map-get($font-sizes, 'm');
    font-weight: bold;
    line-height: 1;
  }

  &.tag--sand {
    background: palette(sand, light);
    border: none;
    border-radius: map-get($border-radiuses, 'l');
    padding: math.div($pad, 4) math.div($pad, 2);
    font-size: map-get($font-sizes, 'm');
  }

  &.tag--bold {
    font-weight: bold;
  }

  &.tag--payback-dark,
  &.tag--payback-light,
  &.tag--payback-light-disabled {
    display: block;
    padding: 2px 6px;
    border-radius: map-get($border-radiuses, 'l');

    &.tag--large {
      text-align: center;
      padding: 3.5px $pad;
    }

    &.tag--removable {
      padding-right: $pad * 2;
    }
  }

  &.tag--payback-dark {
    background-color: palette(payback);
    border-color: palette(payback);
    color: $color-general-text-inverse-default;
    font-size: map-get($font-sizes, 'm');

    .anchor {
      color: $color-general-text-inverse-default;
    }
  }

  &.tag--payback-light-disabled,
  &.tag--payback-light {
    background-color: palette(payback, lighter);
    border-color: palette(payback, lighter);
    color: palette(payback);
    font-size: map-get($font-sizes, 'm');

    .anchor {
      color: palette(payback);
    }
  }

  &.tag--payback-light-disabled {
    background-color: transparent;
    opacity: 0.7;
  }

  &.tag--large {
    font-size: map-get($font-sizes, 'xl');
    font-weight: bold;
  }

  &.tag--new-item {
    font-size: map-get($font-sizes, 'xs');
    border-radius: 0.5em;
    border: 1px solid palette(red);
    color: palette(red);
    padding: 0.15em 0.3em;
    text-transform: uppercase;
    display: inline-block;
    vertical-align: middle;
  }

  &.tag--new-product {
    min-height: $space;
    padding: math.div($pad, 4);
    border: 1px solid $color-brand-fnmz-green;
    background-color: palette(background, green);
    color: $color-brand-fnmz-green;
    font-size: map-get($font-sizes, 's');
    font-weight: map-get($font-weights, 'bold');
  }

  &.tag--info {
    display: flex;
    flex-flow: column wrap;
    align-items: center;
    width: 40px;
    height: 40px;
    background-color: palette(green, light);
    border-radius: 5px;
    border: 0;
    padding: math.div($pad, 3);
  }

  &.tag--new-info {
    display: flex;
    align-items: center;
    justify-content: center;
    color: $color-general-text-inverse-default;
    font-weight: 700;
    width: 40px;
    height: 20px;
    background-color: $color-brand-fnmz-green;
    border-radius: map-get($border-radiuses, 'l');
    border: 0;
    padding: math.div($pad, 2);
  }

  &.tag--advice {
    background: palette(grey, light);
    border-radius: map-get($border-radiuses, 'l');
    border-color: palette(grey, light);
    font-size: map-get($font-sizes, 'l');
    padding: math.div($pad, 4) $pad math.div($pad, 4) 26px;

    &:before {
      content: '';
      width: 8px;
      height: 8px;
      background: $color-general-background-neutral-default;
      position: absolute;
      border-radius: 50%;
      top: 50%;
      transform: translateY(-50%);
      left: 10px;
    }
  }

  &.tag--promotion {
    background-color: $color-general-background-highlight-default;
    color: $color-general-text-promotion-positive-default;
    border-radius: map-get($border-radiuses, 'l');
    padding: 2px 6px;
    font-size: map-get($font-sizes, 's');
    font-weight: map-get($font-weights, 'bold');
    text-transform: uppercase;
    border: none;
  }

  &.tag--discount,
  &.tag--deal {
    background-color: $color-general-background-promotion-discount-default;
    color: $color-general-text-promotion-discount-default;
    border-radius: map-get($border-radiuses, 'l');
    padding: 2px 6px;
    font-size: map-get($font-sizes, 's');
    font-weight: map-get($font-weights, 'bold');
    text-transform: uppercase;
    border: none;
  }

  &.tag--friends {
    background-color: $color-general-background-loyalty-friends-secondary-default;
    color: $color-action-text-inverse-default-base;
    border-radius: map-get($border-radiuses, 'l');
    padding: 2px 6px;
    font-size: map-get($font-sizes, 's');
    font-weight: map-get($font-weights, 'bold');
    text-transform: uppercase;
    border: none;
  }

  &.tag--delivery-type {
    background-color: $color-general-background-status-neutral-default;
    color: $color-general-text-status-neutral-default;
    border-radius: map-get($border-radiuses, 'l');
    padding: 2px 6px;
    font-size: map-get($font-sizes, 's');
    font-weight: map-get($font-weights, 'bold');
    text-transform: uppercase;
    border: none;
  }
}
</style>
